import { SvgIcon } from '@avantia/style-guide';
import type { HpSvgIconTypes } from '@avantia/style-guide';
import { Card, Col, Row } from 'react-bootstrap';

import { analyticsUserType, GTM_EVENT_TYPES, sendAnalytics } from 'analytics';
import { formatDate } from 'utils';
import { PolicyDocument, DocumentTypeCode, DOCUMENT_TYPE_CODE, FullPolicyData } from 'interfaces/customerDataTypes';
import { DOCUMENT_TYPE } from 'interfaces/documentTypes';
import { ExternalLink } from 'components/shared/externalLink';
import { getLinkUrl } from './getLinkUrl';

import './documentCard.scss';
import { useAppSelector } from 'store/hooks';

interface DocumentCardProps {
  selectedPolicy: FullPolicyData;
  policyDocument: PolicyDocument;
  documentType: DocumentTypeCode;
}

const getDocTypeLabel = (type: DocumentTypeCode): DocumentTypeCode => {
  switch (type) {
    case DOCUMENT_TYPE_CODE.RENEWED_DOC:
      return DOCUMENT_TYPE.RENEWED;
    case DOCUMENT_TYPE_CODE.RENEWAL_INVITE_DOC:
      return DOCUMENT_TYPE.RENEWAL_INVITE;
    case DOCUMENT_TYPE_CODE.ADJUSTED_DOC:
      return DOCUMENT_TYPE.ADJUSTED_SCHEDULE;
    case DOCUMENT_TYPE_CODE.BOOKLET:
      return DOCUMENT_TYPE.BOOKLET;
    default:
      return DOCUMENT_TYPE.DOC;
  }
};

export const DocumentCard = ({ selectedPolicy, policyDocument, documentType }: DocumentCardProps): JSX.Element => {
  const { email: policyEmail, policyDetailsId, policyNumber } = selectedPolicy;
  const { effectiveDate, documentArchiveId, endDate, policyBookletUrl, documentTemplateId, historyId } = policyDocument;
  const authentication = useAppSelector((state) => state.authentication);
  const { email, isAgent } = authentication;
  const url = getLinkUrl(
    {
      emailAddress: policyEmail,
      documentArchiveId,
      policyBookletUrl,
      policyDetailsId,
      documentTemplateId,
      historyId,
      policyRef: policyNumber,
      username: email,
      isAgent
    },
    documentType
  );

  const handleClick: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void = (e) => {
    sendAnalytics({
      event: GTM_EVENT_TYPES.DOWNLOAD_DOCUMENTS,
      detail: { document_type: documentType, usertype: analyticsUserType(isAgent) }
    });
    e.preventDefault();
  };

  return (
    // @ts-expect-error handleClick has to be here
    <ExternalLink id={`${getDocTypeLabel(documentType)} id`} className="document-link" href={url} onClick={handleClick}>
      <Card className="container document-card">
        <Row>
          <Col xs={2} className="icon-col">
            <SvgIcon type={'pdf' as HpSvgIconTypes} className="file-icon" />
          </Col>
          <Col xs={10} className="text-col">
            <p className="document-title">{getDocTypeLabel(documentType)}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={{ span: 10, offset: 2 }}>
            <p className="date-range">
              {formatDate(effectiveDate)} - {formatDate(endDate)}
            </p>
          </Col>
        </Row>
      </Card>
    </ExternalLink>
  );
};
